import React from 'react';
import PropTypes from 'prop-types';
import { Body, Meta, Title } from '../../styled components/Typography';
import {
  StyledMessageBar,
  StyledMessageBarHeader,
  StyledMessageBarIcon
} from './MessageBar.style';

function getIcon(variant) {
  if (variant === 'warning') return 'warning';
  if (variant === 'info') return 'feed';
  if (variant === 'success') return 'task-alt';
  return 'error';
}

/**
 * With the Message Bar component you can give feedback to the user.
 * The Message Bar is placed as an element in the flow of the page.
The options of the Message bar are
• 4 feedback types: warning, success, info, errorEach type has its own color and icon
• The message title is optional
• The message can be shown in 2 different sizes, the default size and the small size.
 *  */

const MessageBar = ({
  title = '',
  children,
  variant = 'warning',
  size = 'default',
  className = ''
}) => {
  return (
    <StyledMessageBar $size={size} $variant={variant} className={className}>
      {title && (
        <StyledMessageBarHeader $size={size}>
          <StyledMessageBarIcon
            $iconSize={size === 'small' ? 'medium' : 'large'}
            $variant={variant}
            $messageBarSize={size}
            icon={getIcon(variant)}
          />
          {size === 'small' ? (
            <Body $variant={variant}>{title}</Body>
          ) : (
            <Title $variant={variant} tw="ml-4">
              {title}
            </Title>
          )}
        </StyledMessageBarHeader>
      )}
      {!title && (
        <StyledMessageBarIcon
          $variant={variant}
          $messageBarSize={size}
          $iconSize={size === 'small' ? 'medium' : 'large'}
          icon={getIcon(variant)}
          $inlineWithText
        />
      )}
      {size === 'small' ? (
        <Meta as={title && 'p'}>{children}</Meta>
      ) : (
        <Body>{children}</Body>
      )}
    </StyledMessageBar>
  );
};

MessageBar.propTypes = {
  /* Optional */
  title: PropTypes.string,
  /** The body text of the error message is passed on as a child of the MessageBar component,
   * where it will be wrapped inside the Body/Meta component (determined by the size prop) */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
    .isRequired,
  /* Will determine icon, background color and border color of MessageBar */
  variant: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  /* The small variant for a compact message, preferable without title  */
  size: PropTypes.oneOf(['default', 'small']),
  /** ClassName is supported such that styled components can pass extra styling.
   * Do not use this to adjust the styling, because the ordering of the applied tailwind classes is undeterministic */
  className: PropTypes.string
};

export default MessageBar;
